import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import onboardingService from '@/services/onboardingService'
import { useNavigate } from 'react-router-dom'
import { modalCreators } from '@/store/ducks/modal'
import { useWatchProfile } from '@/hooks/useWatchProfile'
import classNames from 'classnames'
import { twMerge } from 'tailwind-merge'
import Icon from '@/components/atoms/Icon'
import { cohortsRepository } from '@/repositories'

type StepData = {
	progress: 1 | 2 | 3 | 4 | 5
	title: string
	tooltip?: string
	tag?: {
		name: string
	}
	notShowButton?: boolean
	blocked?: boolean
}

export function OnboardingStudents() {
	const dispatch = useAppDispatch()

	const {
		matriculate: { classSelect },
		auth: { user },
	} = useAppSelector((state) => state)

	const [hover, setHover] = useState(false)

	const { refetch } = useWatchProfile()

	const findOnboarding = user?.onboarding_all?.find(
		(onboading) => onboading.curso_id === classSelect?.curso_id
	)

	const findCohort = user?.turmas?.find(
		(cohort) => cohort.curso_id === classSelect?.curso_id
	)

	const navigate = useNavigate()

	const { execute: nextProgress, isLoading } =
		onboardingService.progressStudent()

	const [levelOnboarding, setLevelOnboarding] = useState<
		1 | 2 | 3 | 4 | number
	>(findOnboarding?.nivel_onboarding || 1)
	const [shouldRefetch, setShouldRefetch] = useState(false)

	useEffect(() => {
		if (shouldRefetch) {
			refetch()
			setShouldRefetch(false)
		}
	}, [shouldRefetch, refetch])

	const refetchCohortInShow = async () => {
		await dispatch(cohortsRepository.util.invalidateTags(['Cohorts']))
		refetch()
	}

	useEffect(() => {
		if (findOnboarding?.nivel_onboarding) {
			setLevelOnboarding(findOnboarding?.nivel_onboarding)
		}

		if (findOnboarding?.nivel_onboarding === 4 && findCohort?.id) {
			nextProgress(
				{
					step: findOnboarding?.nivel_onboarding,
					curso_id: classSelect?.curso_id,
				},
				() => refetchCohortInShow()
			)
		}

		if (findOnboarding?.nivel_onboarding === 3 && user?.calendar) {
			nextProgress(
				{
					step: findOnboarding?.nivel_onboarding,
					curso_id: classSelect?.curso_id,
				},
				() => {
					refetch()
					dispatch(modalCreators.closeModal())
				}
			)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [findOnboarding?.nivel_onboarding, user?.calendar])

	const stepsData: StepData[] = [
		{
			progress: 1,
			title: '1 - Assista o Vídeo de Boas vindas',
		},
		{
			progress: 2,
			title: '2 - Realize o Teste de Nivelamento',
		},
		{
			progress: 3,
			title: '3 - Conecte seu Calendário',
		},
		{
			progress: 4,
			title: '4 - Turmas disponíveis',
			tag: { name: 'EM BREVE' },
			blocked: true,
			notShowButton: true,
			tooltip:
				'Quando sua turma estiver disponível, enviaremos um e-mail com mais informações.',
		},
	]

	function dispatchModal(nameModal: string) {
		if (!findOnboarding?.nivel_onboarding) return

		dispatch(
			modalCreators.openModal({
				name: nameModal,
				modalInfo: () =>
					nextProgress(
						{
							step: findOnboarding?.nivel_onboarding,
							curso_id: classSelect?.curso_id,
						},
						() => {
							refetch()
							dispatch(modalCreators.closeModal())
						}
					),
			})
		)
	}

	function dispatchNext(redirect?: string) {
		if (!findOnboarding?.nivel_onboarding) return

		nextProgress(
			{
				step: -1,
				curso_id: classSelect?.curso_id,
			},
			() => {
				refetch()
				redirect && navigate(redirect)
			}
		)
	}

	const nextLevel = (step: 1 | 2 | 3 | 4 | 5) => {
		if (levelOnboarding === step) {
			switch (step) {
				case 1:
					dispatchModal('onboardingWelcomeVideoModal')
					break
				case 2:
					navigate('/curso/nivelamento')
					break
				case 3:
					dispatchModal('onboardingGoogleCalendarModal')
					break
				case 5:
					dispatchNext()
					break
				default:
					break
			}
		}
	}

	if (!findOnboarding?.nivel_onboarding) return <></>

	return (
		<div
			className={twMerge(
				classNames(
					'w-full border-none md:border-solid border-b border-gray-50/5 transition-opacity duration-700',
					{
						'pointer-events-none opacity-10': !findOnboarding?.nivel_onboarding,
					}
				)
			)}
		>
			<div
				className={classNames(
					'flex flex-col relative bg-brand-fluency overflow-clip mb-10 py-6 px-4 md:p-6 xl:p-8 gap-8 rounded-lg',
					{ 'animate-pulse': isLoading }
				)}
			>
				{levelOnboarding === 5 && (
					<button
						onClick={() => nextLevel(levelOnboarding)}
						className='absolute top-3 h-10 w-10 right-3 p-[2px] rounded-full flex items-center justify-center bg-[#4F39A0]/70 hover:bg-[#4F39A0]/90 transition-all duration-200 z-50'
					>
						<Icon
							name='close-modal'
							className='text-white'
							width='20px'
							height='20px'
						/>
					</button>
				)}
				<div className='absolute bottom-0 left-0'>
					<img
						src={`/imgs/admin/onboarding/spray.png`}
						alt='Spray onboarding'
					/>
				</div>
				<div className='absolute top-0 right-0'>
					<img
						src={`/imgs/admin/onboarding/lines.png`}
						alt='Lines onboarding'
					/>
				</div>

				<div className='relative z-10'>
					<h1 className='text-3xl text-white font-bold leading-[110%]'>
						Complete os
						<br /> Primeiros{' '}
						<span className='relative'>
							Passos
							<span className='absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-16 z-[-1]'>
								<img
									src={`/imgs/admin/onboarding/highlight.png`}
									alt='Highlight onboarding'
								/>
							</span>
						</span>
					</h1>
				</div>
				<div className='relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2'>
					{stepsData?.map((step, key) => {
						const isActive = levelOnboarding <= step.progress
						const isCompleted = levelOnboarding > step.progress
						const isClickable =
							levelOnboarding === step.progress || levelOnboarding === 5

						return (
							<div
								key={key}
								className={twMerge(
									classNames(
										'relative py-6 px-4 flex flex-col bg-[#48329A] justify-between rounded-lg h-[188px] opacity-100 cursor-default transition-opacity duration-300',
										{
											'opacity-50': levelOnboarding < step.progress,
											'hidden md:flex':
												levelOnboarding !== step.progress &&
												levelOnboarding !== 5,
											'cursor-pointer': isClickable,
											'hover:opacity-100': isClickable,
										}
									)
								)}
								style={
									levelOnboarding <= step.progress
										? {
												background:
													'linear-gradient(254deg, #342763 7.46%, #3D2887 96.33%)',
											}
										: {}
								}
								onClick={() => isClickable && nextLevel(levelOnboarding)}
								onMouseEnter={() => step.progress === 4 && setHover(true)}
								onMouseLeave={() => step.progress === 4 && setHover(false)}
							>
								<div
									className={twMerge(
										classNames(
											'w-8 h-8 rounded-full flex items-center justify-center border-2 border-white',
											{
												'border-none bg-green shadow-sm':
													levelOnboarding > step.progress,
											}
										)
									)}
								>
									{isCompleted && (
										<Icon
											name='check'
											width='24px'
											height='24px'
											fill='#F7F9FB'
										/>
									)}
								</div>
								<div className='flex flex-col gap-2'>
									{step?.tag && isActive && (
										<div className='py-1 px-3 bg-[#4F39A0] rounded-full flex max-w-max'>
											<span className='font-medium text-sm text-white leading-snug'>
												{step.tag.name}
											</span>
										</div>
									)}
									<div className='flex items-center justify-between'>
										<span className='font-bold text-xl text-white leading-[130%] max-w-[200px]'>
											{step.title}
										</span>
										{levelOnboarding === step.progress &&
											!step?.notShowButton && (
												<div className='flex items-center justify-center w-8 h-8 rounded bg-[#4A3D7A]'>
													<Icon
														name='arrow-right'
														width='24px'
														height='24px'
														fill='#F7F9FB'
													/>
												</div>
											)}
									</div>
								</div>

								{step.tooltip &&
									findOnboarding?.nivel_onboarding &&
									findOnboarding?.nivel_onboarding <= 4 && (
										<div
											className={twMerge(
												'absolute hidden lg:flex w-[120%] -top-16 -right-[10%] -left-[10%] bg-[#312144] py-2 px-4 rounded-lg transition-opacity duration-500 opacity-0',
												classNames({ 'opacity-100': hover })
											)}
										>
											<p className='text-white text-[13px] font-semibold leading-5 text-center'>
												Quando sua turma estiver disponível, enviaremos um
												e-mail com mais informações.
											</p>
										</div>
									)}
							</div>
						)
					})}
				</div>
			</div>
		</div>
	)
}
